import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@shared/lib';

/**
 * Status chip. Soft-tinted backgrounds map a domain status onto a semantic tone
 * (e.g. Settled → success, Excluded → neutral, Private → info). Tokens only.
 */
export const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
  {
    variants: {
      tone: {
        neutral: 'bg-surface text-[#000000]',
        accent: 'bg-accent-soft text-accent',
        info: 'bg-accent-text/10 text-accent-text',
        success: 'bg-success/10 text-success',
        warning: 'bg-warning/10 text-warning',
        danger: 'bg-danger-strong/10 text-danger-strong',
        /** Bordered green pill — "4 Member". */
        member: 'border border-member-border bg-member-bg text-fg',
        /** Bordered peach pill — "Assign member". */
        assign: 'border border-assign-border bg-assign-bg text-assign-fg',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface BadgeProps extends ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
  /** Render as the single child element (Radix Slot) instead of a <span>. */
  asChild?: boolean;
}

export function Badge({ className, tone, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : 'span';
  return <Comp data-slot="badge" className={cn(badgeVariants({ tone }), className)} {...props} />;
}
